import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Cropper, { Area, MediaSize } from "react-easy-crop";
import AppDialog from "../../Components/Dialogs/AppDialog/AppDialog";
import "./MediaUploadPreviewDialog.scss";

interface MediaUploadPreviewDialogProps {
    /** Files pending confirmation, in upload order. Empty/absent hides the dialog. */
    files: File[];
    /** Called once with every file, in order — a photo is replaced by its cropped/resized version, a
     *  video (or anything else) passes through unchanged since there's nothing to edit there. */
    onConfirm: (files: File[]) => void;
    onCancel: () => void;
}

/** Caps the output's longest side — the backend already derives THUMBNAIL/REDUCED variants from whatever's
 *  uploaded, but the original itself is stored as-is, so an oversized phone photo (4000px+) would otherwise
 *  sit in the bucket at full size for no benefit. */
const MAX_OUTPUT_DIMENSION = 2400;

type AspectPresetKey = "original" | "square" | "wide";

const ASPECT_PRESET_LABEL_KEYS: Record<AspectPresetKey, string> = {
    original: "knowledgeBase.form.cropAspectOriginal",
    square: "knowledgeBase.form.cropAspectSquare",
    wide: "knowledgeBase.form.cropAspectWide",
};

const isPhoto = (file: File) => file.type.startsWith("image/") && file.type !== "image/svg+xml";

const JPEG_MIME_TYPE = "image/jpeg";
const PNG_MIME_TYPE = "image/png";
const JPEG_QUALITY = 0.9;
/** The cropper reports whole pixels; a full-size selection can come back a pixel short. */
const CROP_ROUNDING_PX = 1;

/** A PNG stays a PNG (it may have transparency, which JPEG would turn black); everything else becomes JPEG. */
const outputTypeFor = (file: File): { mimeType: string; extension: string } =>
    file.type === PNG_MIME_TYPE ? { mimeType: PNG_MIME_TYPE, extension: "png" } : { mimeType: JPEG_MIME_TYPE, extension: "jpg" };

const withExtension = (filename: string, extension: string): string => {
    const dotIndex = filename.lastIndexOf(".");
    return `${dotIndex > 0 ? filename.slice(0, dotIndex) : filename}.${extension}`;
};

/** Nothing was cut off and nothing needs scaling down: the file the user picked is the best version there is. */
const isUntouched = (image: HTMLImageElement, area: Area): boolean =>
    area.x <= CROP_ROUNDING_PX &&
    area.y <= CROP_ROUNDING_PX &&
    area.width >= image.naturalWidth - CROP_ROUNDING_PX &&
    area.height >= image.naturalHeight - CROP_ROUNDING_PX &&
    Math.max(image.naturalWidth, image.naturalHeight) <= MAX_OUTPUT_DIMENSION;

const cropToFile = (imageSrc: string, area: Area, originalFile: File): Promise<File> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => {
            if (isUntouched(image, area)) {
                resolve(originalFile);
                return;
            }
            const scale = Math.min(1, MAX_OUTPUT_DIMENSION / Math.max(area.width, area.height));
            const canvas = document.createElement("canvas");
            canvas.width = Math.max(1, Math.round(area.width * scale));
            canvas.height = Math.max(1, Math.round(area.height * scale));
            const context = canvas.getContext("2d");
            if (!context) {
                reject(new Error("Canvas 2D context is not available"));
                return;
            }
            context.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, canvas.width, canvas.height);
            const output = outputTypeFor(originalFile);
            canvas.toBlob(
                (blob) => {
                    if (!blob) {
                        reject(new Error("Failed to encode cropped image"));
                        return;
                    }
                    // The name follows the format, or a "photo.png" would hold JPEG bytes.
                    resolve(new File([blob], withExtension(originalFile.name, output.extension), { type: output.mimeType }));
                },
                output.mimeType,
                JPEG_QUALITY
            );
        };
        image.onerror = () => reject(new Error("Failed to load image for cropping"));
        image.src = imageSrc;
    });

/** Shown between picking file(s) and actually uploading them — lets the user preview a photo/video before
 *  it's saved and, for a photo, crop/zoom it first. Steps through `files` one at a time when there's more
 *  than one (a SLIDESHOW section accepts multiple at once); a video only gets a preview, no editing. */
const MediaUploadPreviewDialog: React.FC<MediaUploadPreviewDialogProps> = ({ files, onConfirm, onCancel }) => {
    const { t } = useTranslation();
    const [index, setIndex] = useState(0);
    const [resultFiles, setResultFiles] = useState<File[]>([]);
    const [objectUrl, setObjectUrl] = useState<string | null>(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [aspect, setAspect] = useState(16 / 9);
    const [naturalAspect, setNaturalAspect] = useState(16 / 9);
    const [croppedArea, setCroppedArea] = useState<Area | null>(null);
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        setIndex(0);
        setResultFiles([]);
    }, [files]);

    const currentFile = files[index] ?? null;
    const photo = currentFile ? isPhoto(currentFile) : false;

    useEffect(() => {
        if (!currentFile) {
            setObjectUrl(null);
            return undefined;
        }
        const url = URL.createObjectURL(currentFile);
        setObjectUrl(url);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedArea(null);
        return () => URL.revokeObjectURL(url);
    }, [currentFile]);

    if (files.length === 0 || !currentFile || !objectUrl) {
        return null;
    }

    const advance = (fileToKeep: File) => {
        const next = [...resultFiles, fileToKeep];
        if (index + 1 >= files.length) {
            onConfirm(next);
        } else {
            setResultFiles(next);
            setIndex(index + 1);
        }
    };

    const handleConfirmCurrent = async () => {
        if (!photo || !croppedArea) {
            advance(currentFile);
            return;
        }
        setProcessing(true);
        try {
            advance(await cropToFile(objectUrl, croppedArea, currentFile));
        } catch {
            // Cropping is a client-side nicety — a failure here (e.g. an unusual image format the canvas
            // can't decode) shouldn't block the upload itself, just fall back to the untouched original.
            advance(currentFile);
        } finally {
            setProcessing(false);
        }
    };

    const isLast = index + 1 >= files.length;

    return (
        <AppDialog
            visible
            onHide={onCancel}
            header={t("knowledgeBase.form.previewMediaTitle") as string}
            className="media-upload-preview-dialog"
            footerActions={{
                cancelLabel: t("common.action.cancel") as string,
                onCancel,
                submitLabel: t(isLast ? "common.save" : "common.action.next") as string,
                onSubmit: handleConfirmCurrent,
                submitDisabled: processing,
            }}
        >
            {files.length > 1 && (
                <div className="media-upload-preview-dialog__counter">
                    {index + 1} / {files.length}
                </div>
            )}
            <div className="media-upload-preview-dialog__body">
                {photo ? (
                    <>
                        <div className="media-upload-preview-dialog__crop">
                            <Cropper
                                image={objectUrl}
                                crop={crop}
                                zoom={zoom}
                                aspect={aspect}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={(_area, pixels) => setCroppedArea(pixels)}
                                onMediaLoaded={(mediaSize: MediaSize) => {
                                    const ratio = mediaSize.naturalWidth / mediaSize.naturalHeight;
                                    setNaturalAspect(ratio);
                                    setAspect(ratio);
                                }}
                                objectFit="contain"
                            />
                        </div>
                        <div className="media-upload-preview-dialog__aspect-row">
                            {(
                                [
                                    ["original", naturalAspect],
                                    ["square", 1],
                                    ["wide", 16 / 9],
                                ] as [AspectPresetKey, number][]
                            ).map(([key, value]) => (
                                <button
                                    key={key}
                                    type="button"
                                    className={
                                        "media-upload-preview-dialog__aspect-button" +
                                        (Math.abs(aspect - value) < 0.001 ? " media-upload-preview-dialog__aspect-button--active" : "")
                                    }
                                    onClick={() => setAspect(value)}
                                >
                                    {t(ASPECT_PRESET_LABEL_KEYS[key]) as string}
                                </button>
                            ))}
                        </div>
                        <label className="media-upload-preview-dialog__zoom">
                            <span>{t("knowledgeBase.form.cropZoom") as string}</span>
                            <input
                                className="media-upload-preview-dialog__zoom-slider"
                                type="range"
                                min={1}
                                max={3}
                                step={0.05}
                                value={zoom}
                                onChange={(event) => setZoom(Number(event.target.value))}
                            />
                        </label>
                    </>
                ) : (
                    <video className="media-upload-preview-dialog__video" src={objectUrl} controls />
                )}
            </div>
        </AppDialog>
    );
};

export default MediaUploadPreviewDialog;
